import * as React from 'react';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';

import {UpdateWorkspaceStatus} from '../../../store/partials/Workspaces';
import {UpdatableWorkspaceMeta} from '../../../controller/Workspaces';
import {WorkspaceStatusStates} from '../../../types/enums';
import {Icon} from '../../components/Icon/Icon';
import {ClassNameBuilder} from '../../utils/ClassNameBuilder';
import * as Remove from '../../../assets/MinusCircle.svg';
import * as Save from '../../../assets/Save.svg';
import * as Edit from '../../../assets/Edit.svg';
import * as Add from '../../../assets/PlusCircle.svg';
import * as Close from '../../../assets/CloseCircle.svg';
import * as Update from '../../../assets/CheckCircle.svg';
import * as AnimationStyles from '../../styles/animations.module.scss';

import * as Styles from './WorkspaceListItem.module.scss';
import {CREATE_MODE_ID} from './WorkspacesPanel';

export interface WorkspaceListItemProps {
    onDeleteWorkspace: (id: string) => void;
    onCreateWorkspace?: (id: string, name: string) => void;
    onRestoreWorkspace?: (id: string) => void;
    validateWorkspace: (name: string, id: string) => NameValidationState;
    onCancelCreation: () => void;
    updateWorkspaceStatus: (id: string, status: WorkspaceStatusStates) => void;
    onUpdateWorkspace?: (id: string, meta: UpdatableWorkspaceMeta, shouldUpdateSnapshot: boolean) => void;
    name?: string;
    id: string;
    status: WorkspaceStatusStates;
}

export enum NameValidationState {
    VALID = 'valid',
    INVALID = 'invalid',
    DUPLICATE = 'duplicate',
    EMPTY = 'empty'
}

enum EditState {
    EDIT = 'edit',
    VIEW = 'view',
    CREATE = 'create'
}

const WorkspaceListItemComponent: React.FC<WorkspaceListItemProps> = React.memo(React.forwardRef((props, ref) => {
    const {
        onDeleteWorkspace,
        validateWorkspace,
        onCreateWorkspace,
        onUpdateWorkspace,
        onRestoreWorkspace,
        onCancelCreation,
        name = '',
        id, status,
        updateWorkspaceStatus
    } = props;
    const [proposedName, setProposedName] = React.useState(name);
    const [nameValid, setNameValid] = React.useState(validateWorkspace(name, id));
    const [editState, setEditState] = React.useState(id === CREATE_MODE_ID ? EditState.CREATE : EditState.VIEW);
    const [hasBeenEdited, setHasBeenEdited] = React.useState(false);
    const closeIconStyle = new ClassNameBuilder(Styles, 'round-icon', 'delete-icon');

    const focusRef = React.useCallback((node) => {
        if (node !== null) {
            node.focus();
        }
    }, []);

    React.useEffect(() => {
        return () => {
            if (status === WorkspaceStatusStates.Error) {
                updateWorkspaceStatus(id, WorkspaceStatusStates.None);
            }
        };
    }, [status, editState]);

    const handleCreateOrUpdateWorkspace = () => {
        if (nameValid === NameValidationState.VALID) {
            if (editState === EditState.EDIT) {
                if (name !== proposedName && onUpdateWorkspace) {
                    onUpdateWorkspace(id, {name: proposedName.trim()}, false);
                }
                setEditState(EditState.VIEW);
            } else if (onCreateWorkspace) {
                onCreateWorkspace(id, proposedName.trim());
            }
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProposedName(event.target.value);
        setHasBeenEdited(true);
        if (event.target.value !== '' && event.target.value === name) {
            setNameValid(NameValidationState.VALID);
        } else {
            setNameValid(validateWorkspace(event.target.value, id));
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const key = event.keyCode || event.charCode;
        // key code 13 is enter and 27 is escape
        if (key === 13) {
            handleCreateOrUpdateWorkspace();
            event.stopPropagation();
        } else if (key === 27) {
            setEditState(EditState.VIEW);
            event.stopPropagation();
        }
    };

    const getError = () => {
        let errorString = '';
        if (nameValid === NameValidationState.INVALID) {
            errorString = 'Only allowed characters a-z, A-Z, 0-9, space.';
        } else if (nameValid === NameValidationState.DUPLICATE) {
            errorString = 'Duplicate Name. Please enter another name.';
        } else if (nameValid === NameValidationState.EMPTY) {
            errorString = 'Workspace name cannot be empty.';
        }
        return errorString;
    };

    const renderViewMode = () => {
        const entryClass = new ClassNameBuilder(Styles, 'entry', 'content')
            .add(['loading', status === WorkspaceStatusStates.Loading], AnimationStyles)
            .add(['error', status === WorkspaceStatusStates.Error], AnimationStyles)
            .toString();

        const handleClick = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (onRestoreWorkspace) {
                onRestoreWorkspace(id);
            }
        };

        return (
            <div className={entryClass} onClick={handleClick} ref={ref as React.RefObject<HTMLDivElement>}>
                <Icon src={Remove} className={closeIconStyle.toString()} onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                    event.stopPropagation();
                    onDeleteWorkspace(id);
                }} />
                <span className={Styles['workspace-name']}>{name}</span>
                <Icon src={Edit} className={Styles['round-icon']} onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                    event.stopPropagation();
                    setEditState(EditState.EDIT);
                }} />
                <Icon src={Save} className={Styles['save-icon']} onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                    event.stopPropagation();
                    if (onUpdateWorkspace) {
                        onUpdateWorkspace(id, {}, true);
                    }
                }} />
            </div>
        );
    };

    const onCancelOrClear = () => editState === EditState.CREATE ? onCancelCreation() : setEditState(EditState.VIEW);

    const renderInputMode = () => {
        const handleClick = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (editState !== EditState.CREATE && onRestoreWorkspace) {
                onRestoreWorkspace(id);
            }
        };

        return (
            <div className={ClassNameBuilder.join(Styles, 'content', 'entry')} onClick={handleClick} ref={ref as React.RefObject<HTMLDivElement>}>
                <Icon className={Styles['round-icon']} src={editState === EditState.CREATE ? Add : Update} onClick={handleCreateOrUpdateWorkspace} />
                <span className={Styles['workspace-edit']}>
                    <input type="text"
                        defaultValue={name}
                        ref={focusRef}
                        onChange={handleChange}
                        onKeyDownCapture={handleKeyDown}
                        onBlur={onCancelOrClear}
                    />
                    <Icon
                        src={Close}
                        className={closeIconStyle.toString()}
                        onClick={onCancelOrClear}
                    />
                </span>
                <span className={Styles['error']}>{hasBeenEdited && getError()}</span>
            </div>
        );
    };

    return editState === EditState.VIEW ? renderViewMode() : renderInputMode();
}));

const mapDispatch = (dispatch: Dispatch) => ({
    updateWorkspaceStatus: (id: string, status: WorkspaceStatusStates) => dispatch(new UpdateWorkspaceStatus(id, status))
});

export const WorkspaceListItem = connect(undefined, mapDispatch, undefined, {forwardRef: true})(WorkspaceListItemComponent);
